import React, { useEffect, useState, createElement } from 'react';
import { Star, Sparkles, Heart, Users, FlaskConical, Award, ChevronLeft, ChevronRight, Facebook, Instagram, Youtube } from 'lucide-react';
// --- Curved divider (same shape as /webinar-mc) ---
function CurvedDivider({
  topColor,
  bottomColor,
  direction = 'down'
}: {
  topColor: string;
  bottomColor: string;
  direction?: 'up' | 'down';
}) {
  return <div className="relative w-full overflow-hidden leading-none" style={{
    backgroundColor: topColor
  }} data-id="element-1774">
      <svg className="relative block w-full h-[40px] md:h-[80px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 100" preserveAspectRatio="none" data-id="element-1775">
        {direction === 'down' ? <path d="M0,30 Q600,90 1200,30 L1200,100 L0,100 Z" fill={bottomColor} data-id="element-1776" /> : <path d="M0,70 Q600,10 1200,70 L1200,100 L0,100 Z" fill={bottomColor} data-id="element-1777" />}
      </svg>
    </div>;
}
// --- Small star/sparkle badge used above each section/step ---
function Badge({
  label
}: {
  label: string;
}) {
  return <div className="inline-flex items-center gap-1.5 bg-[#FBF1DC] border border-[#003841]/10 text-[#003841] rounded-full px-3 py-1 text-xs font-semibold shadow-sm" data-id="element-1778">
      <Sparkles className="w-3.5 h-3.5 text-[#E8806E]" data-id="element-1779" />
      <span data-id="element-1780">{label}</span>
    </div>;
}
// --- Reviews data ---
const reviews = [{
  name: 'Jessica Rodijk',
  avatar: 'https://images.unsplash.com/photo-1517849845537-4d257902454a?w=120&h=120&fit=crop',
  title: 'Very enthusiastic',
  body: "I've only been at it for 2 days but I already understand my dog better. Something as simple as putting on a harness now happens without resistance. By focusing more and more on his signals, the interaction keeps getting better and more fun."
}, {
  name: 'Wallie Vuylsteke',
  avatar: 'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=120&h=120&fit=crop',
  title: 'Dog had enough of me after 5 years',
  body: 'This course gave me new insight into my dog. I had no idea how many signals he was already giving me, and unfortunately only sought help when he started growling. By taking a different approach, I now see an enormous change — he seeks contact on his own again and sleeps relaxed next to me. That says everything.',
  trustpilot: true
}, {
  name: 'Nicolette',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop',
  title: 'A must-have, even for experienced dog lovers',
  body: 'We always have rehomed dogs, so we are used to a lot. But with our most recent rescue we really needed help. In a short time we already learned so much and got such clear guidance. It is neatly organized and clear, but what appeals to me most is that it works without commands — through communication.'
}, {
  name: 'Margreet',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop',
  title: 'Walking is a joy again',
  body: 'I learned a lot. It really stands out that because I now carry myself differently, my dogs communicate much more calmly with other dogs. Especially with unfamiliar dogs there used to be a lot of barking. Now barely at all — wonderful. Truly: stressless!'
}];
export function WebinarMCThankYouPage() {
  const [reviewIndex, setReviewIndex] = useState(0);
  // Load Typeform embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//embed.typeform.com/next/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const topBarItems = [{
    icon: Star,
    text: 'Rated Excellent on Trustpilot'
  }, {
    icon: Heart,
    text: 'Punishment-free Method'
  }, {
    icon: FlaskConical,
    text: 'Science-based & Kind'
  }, {
    icon: Users,
    text: '15,000+ Happy Dog Parents'
  }, {
    icon: Award,
    text: 'Trusted Speakers'
  }];
  const visibleReviews = [reviews[reviewIndex % reviews.length], reviews[(reviewIndex + 1) % reviews.length]];
  return <div className="min-h-screen bg-[#FBF1DC] font-sans text-[#0E3A47]" data-id="element-1781">
      {/* Top dark bar */}
      <div className="bg-[#003841] text-white/80 py-2 px-4 overflow-hidden border-b border-white/10" data-id="element-1782">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 md:gap-8 flex-wrap text-xs font-medium" data-id="element-1783">
          {topBarItems.map((item, i) => <div key={i} className="flex items-center gap-1.5 whitespace-nowrap" data-id="element-1784">
              <item.icon className="w-3.5 h-3.5 text-[#95CBCD]" data-id="element-1785" />
              <span data-id="element-1786">{item.text}</span>
            </div>)}
        </div>
      </div>

      {/* Floating pill nav */}
      <nav className="w-full px-4 pt-6 pb-2 flex justify-center" data-id="element-1787">
        <div className="bg-[#FFF9F2] rounded-full px-6 py-3 flex items-center justify-between gap-6 w-full max-w-5xl shadow-lg" data-id="element-1788">
          <img src="https://cdn.magicpatterns.com/uploads/u6HqvoxwaxEaGjC96AKc5M/Logo_Dark_with_Dark_Logotype.svg" alt="Stressless Dogs" className="h-7 md:h-8 object-contain" data-id="element-1789" />
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-[#003841]/80" data-id="element-1790">
            <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1791">
              Courses
            </a>
            <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1792">
              Community
            </a>
            <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1793">
              Success Stories
            </a>
            <a href="#" className="hover:text-[#003841] transition-colors" data-id="element-1794">
              Login
            </a>
          </div>
          <a href="/webinar-mc" className="bg-[#E8806E] hover:bg-[#D26E5C] text-white px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-bold transition-colors shadow-sm flex items-center gap-1.5 whitespace-nowrap" data-id="element-1795">
            Free Webinar <span aria-hidden="true" data-id="element-1796">🐾</span>
          </a>
        </div>
      </nav>

      {/* Hero — confirmation + video */}
      <section className="relative overflow-hidden" data-id="element-1797">
        <img src="https://cdn.magicpatterns.com/uploads/wwgxF2jfrhgpV5vrRDbiAH/Paw_Icon_Warm_White.png" alt="" aria-hidden="true" className="pointer-events-none select-none absolute top-16 right-4 md:top-12 md:-right-16 w-80 md:w-96 opacity-25 -rotate-12 z-0" data-id="element-1798" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 pt-10 md:pt-16 pb-12 md:pb-20 text-center" data-id="element-1799">
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0E3A47] mb-8 leading-tight" data-id="element-1800">
            Yes! You've secured a spot for the Webinar!
          </h1>

          <div className="rounded-2xl overflow-hidden shadow-xl bg-black mb-6" data-id="element-1801">
            <div style={{
            padding: '56.25% 0 0 0',
            position: 'relative'
          }} data-id="element-1802">
              <iframe src="https://player.vimeo.com/video/727758719?h=e9202f5a5e&title=0&byline=0&portrait=0" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Thank you video" data-id="element-1803"></iframe>
            </div>
          </div>

          <div className="text-sm md:text-base text-[#0E3A47]/80 space-y-1 max-w-xl mx-auto" data-id="element-1804">
            <p data-id="element-1805">Have you watched the video above?</p>
            <p data-id="element-1806">
              Then read on below to make sure you get the most out of this
              Webinar...
            </p>
            <p data-id="element-1807">This way you won't miss anything and you'll be well prepared:</p>
          </div>
        </div>
      </section>

      {/* Curved divider into teal */}
      <CurvedDivider topColor="#FBF1DC" bottomColor="#95CBCD" direction="down" data-id="element-1808" />

      {/* Steps section */}
      <section className="bg-[#95CBCD] relative overflow-hidden" data-id="element-1809">
        <img src="https://cdn.magicpatterns.com/uploads/qrxPhSaWZMGbDQ2MBk31kV/Heart_Icon_Warm_White.png" alt="" aria-hidden="true" className="pointer-events-none select-none absolute bottom-16 left-4 md:bottom-12 md:-left-20 w-80 md:w-[28rem] opacity-25 rotate-[15deg] z-0" data-id="element-1810" />

        <div className="relative z-10 max-w-3xl mx-auto px-4 pt-12 md:pt-16 pb-16 md:pb-24" data-id="element-1811">
          <div className="text-center mb-10 md:mb-14" data-id="element-1812">
            <Badge label="Next Up" data-id="element-1813" />
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E3A47] mt-4 leading-tight" data-id="element-1814">
              Follow the steps below!
            </h2>
          </div>

          {/* Step 1 — Typeform */}
          <div className="bg-[#FBF1DC] rounded-3xl p-6 md:p-10 shadow-sm mb-8" data-id="element-1815">
            <div className="text-center mb-6" data-id="element-1816">
              <Badge label="Step 1" data-id="element-1817" />
              <h3 className="text-2xl md:text-3xl font-bold text-[#0E3A47] mt-3 leading-tight" data-id="element-1818">
                Let us know what you'd
                <br data-id="element-1819" />
                like to learn
              </h3>
              <div className="w-12 h-px bg-[#0E3A47]/15 mx-auto mt-5" data-id="element-1820" />
            </div>
            <div className="w-full min-h-[500px]" data-tf-live="01JM04G0YR95FE2CY2XMKEQZQ3" data-id="element-1821"></div>
          </div>

          {/* Step 2 — Check email + calendar */}
          <div className="bg-[#FBF1DC] rounded-3xl p-6 md:p-10 shadow-sm mb-8" data-id="element-1822">
            <div className="text-center mb-6" data-id="element-1823">
              <Badge label="Step 2" data-id="element-1824" />
              <h3 className="text-2xl md:text-3xl font-bold text-[#0E3A47] mt-3 leading-tight" data-id="element-1825">
                Check your email and add the
                <br data-id="element-1826" />
                Webinar to your calendar
              </h3>
              <p className="text-sm text-[#0E3A47]/75 mt-4 max-w-lg mx-auto leading-relaxed" data-id="element-1827">
                You'll receive the email below any moment now (to be safe, also
                add this contact to your favorites):
              </p>
            </div>

            {/* Email contact line */}
            <div className="flex items-center justify-center gap-2 mb-5" data-id="element-1828">
              <span className="w-7 h-7 rounded-full bg-[#E8806E] text-white text-xs font-bold flex items-center justify-center" data-id="element-1829">
                A
              </span>
              <span className="text-sm font-semibold text-[#0E3A47]" data-id="element-1830">
                Aanmelding Masterclass Communiceren
              </span>
            </div>

            <p className="text-sm text-center text-[#0E3A47]/75 mb-6 max-w-md mx-auto" data-id="element-1831">
              Open the email and add the date and time to your calendar. Click
              "Yes" in the calendar invitation if you use a digital calendar.
              <br data-id="element-1832" />
              <span className="text-xs" data-id="element-1833">
                (Below you can see what this looks like)
              </span>
            </p>

            {/* Calendar invite mockup */}
            <div className="bg-white rounded-2xl border border-[#0E3A47]/10 p-4 md:p-5 shadow-sm" data-id="element-1834">
              <div className="flex items-center gap-2 pb-3 border-b border-[#0E3A47]/10 mb-3" data-id="element-1835">
                <span className="font-semibold text-sm text-[#0E3A47]" data-id="element-1836">
                  Aanmelding Masterclass Communiceren
                </span>
                <span className="text-xs text-[#0E3A47]/50" data-id="element-1837">Inbox ×</span>
              </div>
              <div className="flex items-start gap-3 mb-3" data-id="element-1838">
                <span className="w-8 h-8 rounded-full bg-purple-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-1839">
                  A
                </span>
                <div data-id="element-1840">
                  <div className="text-sm font-semibold text-[#0E3A47]" data-id="element-1841">
                    Amee (Stresslessdogs)
                  </div>
                  <div className="text-xs text-[#0E3A47]/60" data-id="element-1842">aan mij ▾</div>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-[#FAFAFA] rounded-lg p-3 border border-[#0E3A47]/5" data-id="element-1843">
                <div className="bg-white rounded-md border border-[#0E3A47]/10 text-center w-14 flex-shrink-0" data-id="element-1844">
                  <div className="bg-[#4285F4] text-white text-[10px] font-bold py-0.5 rounded-t-md" data-id="element-1845">
                    MEI
                  </div>
                  <div className="text-lg font-bold text-[#0E3A47] leading-tight pt-1" data-id="element-1846">
                    21
                  </div>
                  <div className="text-[10px] text-[#0E3A47]/60 pb-1" data-id="element-1847">wo</div>
                </div>
                <div className="text-sm" data-id="element-1848">
                  <div className="font-semibold text-[#0E3A47]" data-id="element-1849">
                    Masterclass Communiceren met je Hond
                  </div>
                  <a href="#" className="text-[#4285F4] text-xs hover:underline" data-id="element-1850">
                    Bekijken op Google Agenda
                  </a>
                  <div className="text-xs text-[#0E3A47]/70 mt-2" data-id="element-1851">
                    <div data-id="element-1852">
                      <span className="font-medium" data-id="element-1853">Wanneer</span> wo 21 mei
                      2025 10:00 – 11:45 (CEST)
                    </div>
                    <div data-id="element-1854">
                      <span className="font-medium" data-id="element-1855">Wie</span>{' '}
                      amee@stresslessdogs.nl*
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-3 flex-wrap" data-id="element-1856">
                    {['Ja', 'Misschien', 'Nee', 'Meer opties'].map(b => <span key={b} className="text-[11px] px-2 py-1 bg-white border border-[#0E3A47]/15 rounded text-[#0E3A47]" data-id="element-1857">
                        {b}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-center text-[#0E3A47]/70 mt-4" data-id="element-1858">
              15 minutes before the Webinar, you'll receive another email with
              the link.
            </p>
          </div>

          {/* Step 3 — During */}
          <div className="bg-[#FBF1DC] rounded-3xl p-6 md:p-10 shadow-sm mb-8" data-id="element-1859">
            <div className="text-center mb-5" data-id="element-1860">
              <Badge label="Step 3" data-id="element-1861" />
              <h3 className="text-2xl md:text-3xl font-bold text-[#0E3A47] mt-3 leading-tight" data-id="element-1862">
                During the Webinar
              </h3>
            </div>
            <div className="text-sm md:text-[15px] text-[#0E3A47]/85 space-y-4 leading-relaxed text-center max-w-xl mx-auto" data-id="element-1863">
              <p data-id="element-1864">
                The Webinar is best followed on a laptop or desktop computer. It
                can also be followed on your phone, but I'll be showing images
                with subtle signals that dogs give. That's why you'll need a
                larger screen than your phone.
              </p>
              <p data-id="element-1865">
                During the Webinar, you can see and hear me, but I cannot see or
                hear you (or any other attendees). You can actively participate
                by answering the quiz questions that come up during the session,
                but even then I cannot see or hear you.
              </p>
              <p className="font-semibold" data-id="element-1866">
                Make sure you have a quiet place to follow the Webinar, with as
                few distractions as possible and a good internet connection.
              </p>
            </div>
          </div>

          {/* Step 4 — Social */}
          <div className="bg-[#FBF1DC] rounded-3xl p-6 md:p-10 shadow-sm mb-8" data-id="element-1867">
            <div className="text-center mb-5" data-id="element-1868">
              <Badge label="Step 4" data-id="element-1869" />
              <h3 className="text-2xl md:text-3xl font-bold text-[#0E3A47] mt-3 leading-tight" data-id="element-1870">
                Follow me on social media
              </h3>
              <p className="text-sm text-[#0E3A47]/75 mt-3 max-w-md mx-auto leading-relaxed" data-id="element-1871">
                On social media, I share daily information about how to live
                together with your dog in a natural and loving way. If you enjoy
                that, feel free to follow me there :)
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-5" data-id="element-1872">
              {[{
              label: 'Facebook',
              href: 'https://www.facebook.com/stresslessdogs.nl/',
              Icon: Facebook
            }, {
              label: 'Instagram',
              href: 'https://www.instagram.com/stresslessdogs.nl/',
              Icon: Instagram
            }, {
              label: 'YouTube',
              href: 'https://www.youtube.com/@stresslessdogs',
              Icon: Youtube
            }, {
              label: 'TikTok',
              href: 'https://www.tiktok.com/@stresslessdogs.nl',
              Icon: null
            }].map(s => <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="bg-[#003841] hover:bg-[#0E3A47] text-white text-sm font-semibold px-5 py-2 rounded-full flex items-center gap-2 transition-colors" data-id="element-1873">
                  {s.Icon && <s.Icon className="w-4 h-4" data-id="element-1874" />}
                  {s.label}
                </a>)}
            </div>
          </div>

          {/* Step 5 — Reviews */}
          <div className="bg-[#FBF1DC] rounded-3xl p-6 md:p-10 shadow-sm" data-id="element-1875">
            <div className="text-center mb-6" data-id="element-1876">
              <Badge label="Step 5" data-id="element-1877" />
              <h3 className="text-2xl md:text-3xl font-bold text-[#0E3A47] mt-3 leading-tight" data-id="element-1878">
                Read reviews
              </h3>
              <p className="text-sm text-[#0E3A47]/75 mt-3" data-id="element-1879">
                Below, you can read what other dog owners thought of it:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5" data-id="element-1880">
              {visibleReviews.map((r, i) => <div key={i} className="bg-[#FDF6E8] border border-[#0E3A47]/10 rounded-2xl p-5 text-left" data-id="element-1881">
                  {r.trustpilot && <div className="flex items-center gap-2 mb-3" data-id="element-1882">
                      <div className="flex text-[#00B67A]" data-id="element-1883">
                        {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-current" data-id="element-1884" />)}
                      </div>
                      <span className="text-xs text-[#0E3A47]/60 font-semibold" data-id="element-1885">
                        ★ Trustpilot
                      </span>
                    </div>}
                  <h4 className="font-bold text-[#0E3A47] mb-2" data-id="element-1886">{r.title}</h4>
                  <p className="text-sm text-[#0E3A47]/80 leading-relaxed" data-id="element-1887">
                    {r.body}
                  </p>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#0E3A47]/10" data-id="element-1888">
                    <img src={r.avatar} alt={r.name} className="w-9 h-9 rounded-full object-cover" data-id="element-1889" />
                    <span className="text-sm font-semibold text-[#0E3A47]" data-id="element-1890">
                      {r.name}
                    </span>
                  </div>
                </div>)}
            </div>

            <div className="flex justify-center gap-3 mt-6" data-id="element-1891">
              <button onClick={() => setReviewIndex((reviewIndex - 1 + reviews.length) % reviews.length)} aria-label="Previous reviews" className="w-9 h-9 rounded-full bg-[#003841] text-white flex items-center justify-center hover:bg-[#0E3A47] transition-colors" data-id="element-1892">
                <ChevronLeft className="w-4 h-4" data-id="element-1893" />
              </button>
              <button onClick={() => setReviewIndex((reviewIndex + 1) % reviews.length)} aria-label="Next reviews" className="w-9 h-9 rounded-full bg-[#003841] text-white flex items-center justify-center hover:bg-[#0E3A47] transition-colors" data-id="element-1894">
                <ChevronRight className="w-4 h-4" data-id="element-1895" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Dog photo strip transition */}
      <div className="relative bg-[#95CBCD] overflow-hidden" data-id="element-1896">
        <div className="h-32 md:h-44 bg-[#003841] relative" data-id="element-1897">
          {/* Dogs peeking up from the dark band */}
          <div className="absolute inset-x-0 -top-16 md:-top-24 flex justify-center items-end gap-4 md:gap-10 max-w-3xl mx-auto px-4" data-id="element-1898">
            <img src="https://images.unsplash.com/photo-1552053831-71594a27632d?w=300&h=300&fit=crop" alt="" aria-hidden="true" className="w-24 h-24 md:w-36 md:h-36 rounded-full object-cover border-4 border-[#003841] shadow-lg" data-id="element-1899" />
            <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=300&h=300&fit=crop" alt="" aria-hidden="true" className="w-28 h-28 md:w-44 md:h-44 rounded-full object-cover border-4 border-[#003841] shadow-lg" data-id="element-1900" />
            <img src="https://images.unsplash.com/photo-1517423440428-a5a00ad493e8?w=300&h=300&fit=crop" alt="" aria-hidden="true" className="w-24 h-24 md:w-36 md:h-36 rounded-full object-cover border-4 border-[#003841] shadow-lg" data-id="element-1901" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#F5E6CC] relative overflow-hidden" data-id="element-1902">
        <img src="https://cdn.magicpatterns.com/uploads/9WXb2ivRHstKthbdCMaxCu/Dog_Icon_Warm_White.png" alt="" aria-hidden="true" className="pointer-events-none select-none absolute bottom-[2px] left-1/2 -translate-x-1/2 md:bottom-[2px] w-60 md:w-[21rem] opacity-25 rotate-[8deg] z-0" data-id="element-1903" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 pt-12 pb-10 grid md:grid-cols-2 gap-8" data-id="element-1904">
          <div data-id="element-1905">
            <img src="https://cdn.magicpatterns.com/uploads/u6HqvoxwaxEaGjC96AKc5M/Logo_Dark_with_Dark_Logotype.svg" alt="Stressless Dogs" className="h-8 object-contain mb-4" data-id="element-1906" />
            <p className="text-sm text-[#0E3A47]/75 max-w-sm leading-relaxed" data-id="element-1907">
              Explore a new way of living with your dog, based on understanding,
              communication and trust — not commands and corrections.
            </p>
          </div>
          <div className="md:text-right" data-id="element-1908">
            <h4 className="text-sm font-bold text-[#0E3A47] mb-3" data-id="element-1909">
              Quick Links
            </h4>
            <ul className="space-y-1.5 text-sm text-[#0E3A47]/80" data-id="element-1910">
              <li data-id="element-1911">
                <a href="#" className="hover:text-[#E8806E] transition-colors" data-id="element-1912">
                  Courses
                </a>
              </li>
              <li data-id="element-1913">
                <a href="#" className="hover:text-[#E8806E] transition-colors" data-id="element-1914">
                  Community
                </a>
              </li>
              <li data-id="element-1915">
                <a href="#" className="hover:text-[#E8806E] transition-colors" data-id="element-1916">
                  Success Stories
                </a>
              </li>
              <li data-id="element-1917">
                <a href="#" className="hover:text-[#E8806E] transition-colors" data-id="element-1918">
                  Login
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="relative z-10 border-t border-[#0E3A47]/10" data-id="element-1919">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-[#0E3A47]/60" data-id="element-1920">
            <p data-id="element-1921">© 2026 Stressless Dogs • All rights reserved</p>
            <div className="flex gap-4" data-id="element-1922">
              <a href="#" className="hover:text-[#E8806E] transition-colors" data-id="element-1923">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-[#E8806E] transition-colors" data-id="element-1924">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}